import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { reduxForm, Field } from 'redux-form';
import { noop } from 'lodash';

import config from 'config';
import { Button } from 'shared/components';
import { FormError, Input } from 'shared/components/reduxFormComponents';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { INTERNAL_WITH_PHONE, PHONE_NUMBER_LENGTH } from 'modules/auth/actions/loginForm';
import { apiErrorMessagesMap } from 'modules/auth/constants/apiErrorCodes';

import theme from '../../LoginForm.scss';

const propTypes = {
  intl: PropTypes.object.isRequired,
  submitFormStatus: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  showExtendedForm: PropTypes.func,
  untouch: PropTypes.func,
  recaptchaRequired: PropTypes.bool,
  loginMethod: PropTypes.string,
  loginConfig: PropTypes.object,
  error: PropTypes.any,
};

const defaultProps = {
  submitFormStatus: requestsStatuses.default,
  onSubmit: noop,
  showExtendedForm: noop,
  untouch: noop,
  recaptchaRequired: false,
  loginMethod: undefined,
  loginConfig: {
    secondaryLoginFields: [],
    externalIdName: 'ID',
    loginText: '',
    externalIdNamePlaceholder: '',
  },
  error: null,
};

const removeFocus = () => document.activeElement.blur();

const PrimaryLoginForm = ({
  intl,
  submitFormStatus,
  handleSubmit,
  onSubmit,
  showExtendedForm,
  recaptchaRequired,
  loginConfig,
  loginMethod,
  untouch,
  error,
}) => {
  return (
    <div>
      <h2 className={ theme.title }>
        { loginConfig.loginText }
      </h2>
      <form
        className={ theme.form }
        onSubmit={ handleSubmit(onSubmit) }
      >
        <Field
          name="externalId"
          testId="login-policy-id-field"
          component={ Input }
          rootClassName={ theme.input }
          ariaLabel={{ id: 'General.IDNumber', defaultMessage: '** ID Number' }}
          placeholder={ loginConfig.externalIdNamePlaceholder }
          iconName={ config.product_name !== 'clalit' ? 'patient-info' : undefined }
          iconPositionLeft={ true }
          iconPositionRtlNotReversed={ true }
          greenSuccessStatus={ true }
          size="large"
          mediaResized={ true }
          validatePattern="^[0-9]+$"
          inputMode="numeric"
          disabled={ submitFormStatus === requestsStatuses.pending || submitFormStatus === requestsStatuses.success }
          tooltipTestId="login-form-policyId-tooltip"
          highlightTextOnError={ config.product_name === 'clalit' }
          onBlur={ () => untouch('externalId') }
          errorMessagesMap={ apiErrorMessagesMap }
          autoComplete="off"
        />

        { loginMethod === INTERNAL_WITH_PHONE && (
          <Field
            name="phoneNumber"
            testId="login-phone-number-field"
            component={ Input }
            rootClassName={ theme.input }
            placeholder={{ id: 'General.PhoneNum', defaultMessage: '** Phone Number' }}
            iconName={ config.product_name !== 'clalit' ? 'phone' : undefined }
            iconPositionLeft={ true }
            iconPositionRtlNotReversed={ true }
            greenSuccessStatus={ true }
            size="large"
            mediaResized={ true }
            validatePattern="^[0-9]+$"
            maxLength={ PHONE_NUMBER_LENGTH }
            highlightTextOnError={ config.product_name === 'clalit' }
            disabled={ submitFormStatus === requestsStatuses.pending || submitFormStatus === requestsStatuses.success }
            onBlur={ () => untouch('phoneNumber') }
            errorMessagesMap={ apiErrorMessagesMap }
          />
        ) }

        <If condition={ loginConfig.secondaryLoginFields.length }>
          <div
            data-testId="login-dont-have-id-number"
            onClick={ showExtendedForm }
            className={ theme.link }
          >
            <FormattedMessage
              id="Authentication.Login.Patient.Label.DontHaveIdNumber"
              defaultMessage="** I don't have ID Number"
            />
          </div>
        </If>

        <If condition={ error }>
          <FormError error={ error } className={ theme.error } />
        </If>

        <Button
          testId="login-button"
          label={ intl.formatMessage({ id: 'General.Next', defaultMessage: '** Next' }) }
          iconName="arrow-right"
          isLoading={ submitFormStatus === requestsStatuses.pending || submitFormStatus === requestsStatuses.success }
          size="large"
          disabled={
            submitFormStatus === requestsStatuses.pending
            || submitFormStatus === requestsStatuses.success
            || recaptchaRequired
          }
          mediaResized
          fullWidth
          onClick={ removeFocus }
        />
      </form>
    </div>
  );
};

PrimaryLoginForm.propTypes = propTypes;
PrimaryLoginForm.defaultProps = defaultProps;

export default reduxForm({
  touchOnBlur: false,
})(PrimaryLoginForm);
